import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  LocationOff as LocationOffIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import locationService from "../services/locationService";
import LocationTroubleshooting from "./LocationTroubleshooting";

const LocationPermission = ({ onLocationUpdate, onPermissionChange }) => {
  const [permissionStatus, setPermissionStatus] = useState("unknown");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(true);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  useEffect(() => {
    setIsSupported(locationService.isSupported);
    checkPermissionStatus();

    // Subscribe to location changes
    const unsubscribe = locationService.subscribe((location) => {
      setCurrentLocation(location);
      if (onLocationUpdate) {
        onLocationUpdate(location);
      }
    });

    // Note: Auto-request is now handled by LocationStartupNotification component

    return unsubscribe;
  }, [onLocationUpdate]);

  const checkPermissionStatus = () => {
    const status = locationService.getPermissionStatus();
    setPermissionStatus(status);

    if (locationService.hasLocation()) {
      setCurrentLocation(locationService.currentLocation);
    }
  };

  const requestLocationPermission = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const location = await locationService.requestLocationPermission();
      setCurrentLocation(location);
      setPermissionStatus("granted");

      if (onPermissionChange) {
        onPermissionChange("granted", location);
      }
    } catch (error) {
      setError(error.message);
      setPermissionStatus("denied");
      setShowTroubleshooting(true);

      if (onPermissionChange) {
        onPermissionChange("denied", null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshLocation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const location = await locationService.requestLocationPermission();
      setCurrentLocation(location);
      setPermissionStatus("granted");

      if (onPermissionChange) {
        onPermissionChange("granted", location);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const clearLocation = () => {
    locationService.clearStoredLocation();
    setCurrentLocation(null);
    setPermissionStatus("unknown");
    setShowTroubleshooting(false);

    if (onPermissionChange) {
      onPermissionChange("unknown", null);
    }
  };

  const handleLocationFixed = (location) => {
    setCurrentLocation(location);
    setPermissionStatus("granted");
    setError(null);
    setShowTroubleshooting(false);

    if (onLocationUpdate) {
      onLocationUpdate(location);
    }
    if (onPermissionChange) {
      onPermissionChange("granted", location);
    }
  };

  const getStatusIcon = () => {
    switch (permissionStatus) {
      case "granted":
        return <CheckCircleIcon color="success" />;
      case "denied":
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getStatusColor = () => {
    switch (permissionStatus) {
      case "granted":
        return "success";
      case "denied":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = () => {
    switch (permissionStatus) {
      case "granted":
        return "Location Access Granted";
      case "denied":
        return "Location Access Denied";
      default:
        return "Location Access Unknown";
    }
  };

  if (!isSupported) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <LocationOffIcon color="error" />
            <Typography variant="h6">Location Not Supported</Typography>
          </Box>
          <Alert severity="warning">
            Your browser doesn't support geolocation. Arya won't be able to
            provide location-based weather information.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6">Location Services</Typography>
          </Box>
          <Chip
            icon={getStatusIcon()}
            label={getStatusText()}
            color={getStatusColor()}
            variant="outlined"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {permissionStatus === "unknown" && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Allow Arya to access your location to provide personalized weather
              information and location-based recommendations.
            </Typography>
            <Button
              variant="contained"
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <LocationIcon />
              }
              onClick={requestLocationPermission}
              disabled={isLoading}
              fullWidth
            >
              {isLoading
                ? "Requesting Permission..."
                : "Enable Location Access"}
            </Button>
          </Box>
        )}

        {permissionStatus === "denied" && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Location access was denied. You can still use Arya, but weather
              information will be limited.
            </Alert>
            <Button
              variant="outlined"
              startIcon={<LocationIcon />}
              onClick={requestLocationPermission}
              disabled={isLoading}
              fullWidth
            >
              Try Again
            </Button>
          </Box>
        )}

        {permissionStatus === "granted" && currentLocation && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Location access granted! Arya can now provide personalized weather
              information.
            </Alert>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Current Location
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {currentLocation.latitude.toFixed(4)},{" "}
                  {currentLocation.longitude.toFixed(4)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Accuracy: ±{Math.round(currentLocation.accuracy)}m
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Tooltip title="Refresh Location">
                  <IconButton
                    size="small"
                    onClick={refreshLocation}
                    disabled={isLoading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear Location">
                  <IconButton
                    size="small"
                    onClick={clearLocation}
                    color="error"
                  >
                    <LocationOffIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Last updated:{" "}
              {new Date(currentLocation.timestamp).toLocaleString()}
            </Typography>
          </Box>
        )}

        {isLoading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={2}
          >
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Getting your location...
            </Typography>
          </Box>
        )}

        {/* Show troubleshooting component when there are permission issues */}
        {showTroubleshooting && (
          <Box mt={2}>
            <LocationTroubleshooting onLocationFixed={handleLocationFixed} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default LocationPermission;
